import { FileCode, FileImage, Folder } from "lucide-react"
import { twMerge } from "tailwind-merge"
import { FilePathContextMenu } from "../FilePathActions"
import { VirtualizedFixedList } from "../ui/VirtualizedFixedList"
import type { FlatTreeEntry } from "../utils/changesTree"
import { joinPath } from "../utils/paths"
import { StatusIcon } from "./shared"

interface ChangedFilesTreeItemProps {
    entry: FlatTreeEntry
    selected: boolean
    onSelect: () => void
    compact: boolean
    workingDir: string
}

function ChangedFilesTreeItem({ entry, selected, onSelect, compact, workingDir }: ChangedFilesTreeItemProps) {
    const { node, depth } = entry
    const indentation = depth * (compact ? 8 : 10) + 6
    const absolutePath = joinPath(workingDir, node.path)

    if (node.isDir) {
        return (
            <FilePathContextMenu path={absolutePath} workingDir={workingDir} isDirectory>
                <div
                    className={twMerge("flex w-full items-center gap-1 pr-2 text-left text-sm text-muted", compact ? "h-10 py-1" : "py-1")}
                    style={{ paddingLeft: `${indentation}px` }}
                    title={node.path}
                >
                    <Folder size={14} className="flex-shrink-0 text-muted" />
                    <span className="truncate font-mono text-xs">{node.name}</span>
                    <span className="ml-auto flex-shrink-0 text-xs text-muted">{node.fileCount}</span>
                </div>
            </FilePathContextMenu>
        )
    }

    if (!node.file) {
        return null
    }

    const file = node.file
    const FileIcon = file.binary ? FileImage : FileCode

    return (
        <FilePathContextMenu path={absolutePath} workingDir={workingDir}>
            <button
                type="button"
                onClick={onSelect}
                aria-current={selected ? "true" : undefined}
                className={twMerge(
                    "btn flex w-full items-center gap-1 py-1 pr-2 text-left text-sm transition-colors",
                    compact && "h-10 py-1",
                    selected ? "bg-primary/10 text-base-content" : "text-muted hover:bg-base-200 hover:text-base-content"
                )}
                style={{ paddingLeft: `${indentation}px` }}
                title={file.binary ? `${file.path} (binary)` : file.path}
            >
                <StatusIcon status={file.status} />
                <FileIcon size={14} className="flex-shrink-0 text-muted" />
                <span className="truncate font-mono text-xs">{node.name}</span>
            </button>
        </FilePathContextMenu>
    )
}

interface ChangedFilesTreeProps {
    entries: FlatTreeEntry[]
    selectedFilePath: string | null
    workingDir: string
    onSelect: (path: string) => void
    className?: string
    compact?: boolean
}

export function ChangedFilesTree({ entries, selectedFilePath, workingDir, onSelect, className, compact = false }: ChangedFilesTreeProps) {
    const selectedIndex = selectedFilePath ? entries.findIndex((entry) => !entry.node.isDir && entry.node.file?.path === selectedFilePath) : undefined

    return (
        <VirtualizedFixedList
            items={entries}
            rowHeight={compact ? 40 : 30}
            className={twMerge("overflow-y-auto", className)}
            scrollToIndex={selectedIndex === -1 ? undefined : selectedIndex}
            renderRow={(entry) => (
                <div className={compact ? undefined : "py-1"}>
                    <ChangedFilesTreeItem
                        entry={entry}
                        selected={!entry.node.isDir && entry.node.file?.path === selectedFilePath}
                        onSelect={() => {
                            if (!entry.node.isDir && entry.node.file) {
                                onSelect(entry.node.file.path)
                            }
                        }}
                        compact={compact}
                        workingDir={workingDir}
                    />
                </div>
            )}
        />
    )
}
